// money.ts — integer-cent / basis-point arithmetic primitives.
//
// D-003: all tax-path arithmetic happens in integer euro cents, rates in integer
// basis points, and every taxable-event multiplication goes through mulDiv, which is
// exact (BigInt-based, no intermediate float rounding) and rounds half away from zero
// ("kaufmännisch"). This avoids the classic float-rounding class of bug: in float64,
// `1.005 * 100 === 100.49999999999999`, so naive `Math.round(1.005 * 100)` yields 100
// where the true value 100.5 must round to 101. mulDiv computes such quantities in
// exact integer arithmetic, so the half-cent boundary is always decided correctly.
const MAX_SAFE_BIG = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE_BIG = BigInt(Number.MIN_SAFE_INTEGER);
function assertSafeInteger(x, name) {
    if (!Number.isSafeInteger(x)) {
        throw new RangeError(`mulDiv: argument "${name}" = ${x} is not a safe integer`);
    }
}
/**
 * Exact integer `v * num / den`, rounded half away from zero.
 *
 * Implemented entirely in BigInt so there is no intermediate float rounding — the
 * multiplication and division are exact, and only the final rounding step is applied.
 * Inputs must be safe integers (asserted); the product is guarded to stay within
 * Number.MAX_SAFE_INTEGER, since callers in this codebase never approach 2^53.
 */
export function mulDiv(v, num, den) {
    assertSafeInteger(v, 'v');
    assertSafeInteger(num, 'num');
    assertSafeInteger(den, 'den');
    if (den === 0) {
        throw new RangeError('mulDiv: division by zero (den === 0)');
    }
    const bv = BigInt(v);
    const bn = BigInt(num);
    const bd = BigInt(den);
    const product = bv * bn;
    if (product > MAX_SAFE_BIG || product < MIN_SAFE_BIG) {
        throw new RangeError(`mulDiv: intermediate product ${product.toString()} exceeds Number.MAX_SAFE_INTEGER`);
    }
    const rounded = divRoundHalfAwayFromZero(product, bd);
    return Number(rounded);
}
function divRoundHalfAwayFromZero(numerator, denominator) {
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const q = n / d;
    const r = n % d;
    // r/d >= 1/2  <=>  2r >= d
    const roundedAbs = 2n * r >= d ? q + 1n : q;
    return negative ? -roundedAbs : roundedAbs;
}
/** Round a plain (non-mulDiv) numeric cent value half away from zero to an integer. */
export function roundCentsHalfAwayFromZero(x) {
    return x >= 0 ? Math.floor(x + 0.5) : -Math.floor(-x + 0.5);
}
//# sourceMappingURL=money.js.map